import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { Form as FinalForm, Field } from 'react-final-form';
import { useHistory } from 'react-router-dom';

import { useIntl } from '../../../../../util/reactIntl';
import { useRouteConfiguration } from '../../../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../../../util/routes';

import { Form } from '../../../../../components';
import IconSearch from '../../icons/IconSearch';

import css from './TopbarSearchForm.module.css';

/**
 * Compact, topbar-friendly keyword search. Mirrors the HeroSearchForm
 * submit behaviour (navigates to SearchPage; appends ?keywords=... when
 * non-empty) but renders as a single horizontal field sized to drop into
 * the topbar's centred logo slot when the search icon is toggled open.
 *
 * Auto-focuses the input on mount so the keyboard is ready to type
 * immediately after the toggle. Submit closes the inline form via the
 * parent's onClose, but the navigation that follows also unmounts the
 * topbar slot so the explicit call is a safety net for cases where the
 * user submits an empty query (lands on /s with no keywords).
 */
const TopbarSearchForm = props => {
  const { onClose, className, placeholder } = props;
  const intl = useIntl();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const onSubmit = values => {
    const keywords = values?.keywords?.trim();
    const searchParams = keywords ? { keywords } : {};
    const url = createResourceLocatorString(
      'SearchPage',
      routeConfiguration,
      {},
      searchParams
    );
    history.push(url);
    if (onClose) {
      onClose();
    }
  };

  return (
    <FinalForm
      onSubmit={onSubmit}
      render={({ handleSubmit }) => (
        <Form
          className={classNames(css.form, className)}
          onSubmit={handleSubmit}
          enforcePagePreloadFor="SearchPage"
        >
          <button
            type="submit"
            className={css.submit}
            aria-label={intl.formatMessage({ id: 'Topbar.searchSubmit' })}
          >
            <IconSearch className={css.icon} />
          </button>
          <Field
            name="keywords"
            render={({ input }) => (
              <input
                {...input}
                ref={inputRef}
                type="text"
                className={css.input}
                placeholder={placeholder || intl.formatMessage({ id: 'Topbar.searchPlaceholder' })}
                autoComplete="off"
              />
            )}
          />
          <button
            type="submit"
            className={css.submitCircle}
            aria-label={intl.formatMessage({ id: 'Topbar.searchSubmit' })}
          >
            <svg
              className={css.submitCircleIcon}
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 8h10M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </Form>
      )}
    />
  );
};

export default TopbarSearchForm;
